import React, { useCallback, useEffect, useRef, useState } from 'react';
import { contactApi } from '../services';
import documentsApi from '../services/documentsApi';
import { formatDateTime, timeAgo } from '../utils/time';
import AccordionSection from './AccordionSection';
import { useAuth } from './AuthGate';
import DocumentSuggestionCard from './DocumentSuggestionCard';
import Icon from './Icon';
import {
  DOCUMENT_DATE_FIELDS, DOCUMENT_KIND_LABELS, DOCUMENT_STATUS_LABELS, expiryText, isDriveLink, statusPillText
} from './documentUtils';
import './RelationshipManagers.css';
import './CompanyDocuments.css';

const TEXT_FIELDS = ['kind', 'title', 'status', 'drive_url', 'notes'];
const EMPTY_FORM = {
  kind: 'nda', title: '', status: 'sent', contact_id: '', drive_url: '', notes: '',
  ...Object.fromEntries(DOCUMENT_DATE_FIELDS.map(([key]) => [key, '']))
};

const toForm = (document) => (document ? {
  ...EMPTY_FORM,
  ...Object.fromEntries([...TEXT_FIELDS, ...DOCUMENT_DATE_FIELDS.map(([key]) => key)]
    .map(key => [key, document[key] || ''])),
  contact_id: document.contact_id ? String(document.contact_id) : ''
} : EMPTY_FORM);

// What to send: everything that has a value when adding, only what changed when editing
// (empty strings clear a field)
export const fromForm = (form, document) => {
  const values = { ...form, contact_id: form.contact_id ? Number(form.contact_id) : null };
  const before = toForm(document);
  const sent = {};
  Object.keys(values).forEach(key => {
    const unchanged = document ? form[key] === before[key] : (values[key] === '' || values[key] === null);
    if (!unchanged) sent[key] = values[key];
  });
  return sent;
};

const DocumentForm = ({ companyId, document, onSaved, onCancel, onDeleted }) => {
  const { isAdmin } = useAuth();
  const [form, setForm] = useState(() => toForm(document));
  const [people, setPeople] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);
  const titleRef = useRef(null);
  const id = (field) => `doc-form-${document ? document.id : 'new'}-${field}`;

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    contactApi.getCompanyContacts(companyId)
      .then(data => { if (!cancelled) setPeople(data.contacts || []); })
      // Only for picking a contact: the form still works without them
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const update = (field) => (event) => setForm(current => ({ ...current, [field]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      setError('A title is required');
      return;
    }
    const values = fromForm({ ...form, title: form.title.trim() }, document);
    if (document && Object.keys(values).length === 0) {
      onCancel();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      onSaved(document
        ? await documentsApi.update(document.id, values)
        : await documentsApi.create(companyId, values));
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    setError(null);
    try {
      await documentsApi.remove(document.id);
      onDeleted(document);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const contactMissing = form.contact_id && !people.some(person => String(person.id) === form.contact_id);

  return (
    <form className="cdoc-form" onSubmit={submit}>
      <div className="cdoc-form__row">
        <label className="cdoc-field" htmlFor={id('kind')}>
          <span>Kind</span>
          <select id={id('kind')} className="rm-input" value={form.kind} onChange={update('kind')} disabled={saving}>
            {Object.entries(DOCUMENT_KIND_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label className="cdoc-field" htmlFor={id('status')}>
          <span>Status</span>
          <select id={id('status')} className="rm-input" value={form.status} onChange={update('status')} disabled={saving}>
            {Object.entries(DOCUMENT_STATUS_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
      </div>
      <label className="cdoc-field" htmlFor={id('title')}>
        <span>Title</span>
        <input
          id={id('title')}
          ref={titleRef}
          className="rm-input"
          value={form.title}
          onChange={update('title')}
          maxLength={200}
          placeholder="Name on the document"
          disabled={saving}
        />
      </label>
      <label className="cdoc-field" htmlFor={id('contact')}>
        <span>With</span>
        <select id={id('contact')} className="rm-input" value={form.contact_id} onChange={update('contact_id')} disabled={saving}>
          <option value="">Nobody in particular</option>
          {contactMissing && <option value={form.contact_id}>{document?.contact_name || 'Unknown person'}</option>}
          {people.map(person => (
            <option key={person.id} value={person.id}>{person.name || 'Unnamed'}{person.title ? `, ${person.title}` : ''}</option>
          ))}
        </select>
      </label>
      <label className="cdoc-field" htmlFor={id('drive_url')}>
        <span>Drive link</span>
        <input
          id={id('drive_url')}
          className="rm-input"
          type="url"
          value={form.drive_url}
          onChange={update('drive_url')}
          maxLength={2000}
          placeholder="https://drive.google.com/..."
          disabled={saving}
        />
      </label>
      <div className="cdoc-form__dates">
        {DOCUMENT_DATE_FIELDS.map(([key, label]) => (
          <label key={key} className="cdoc-field" htmlFor={id(key)}>
            <span>{label}</span>
            <input id={id(key)} className="rm-input" type="date" value={form[key]} onChange={update(key)} disabled={saving} />
          </label>
        ))}
      </div>
      <label className="cdoc-field" htmlFor={id('notes')}>
        <span>Notes</span>
        <textarea id={id('notes')} className="rm-input" rows={2} value={form.notes} onChange={update('notes')}
          maxLength={4000} disabled={saving} />
      </label>

      {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}

      <div className="rm-buttons">
        <button type="submit" className="rm-button rm-button--primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button type="button" className="rm-button" onClick={onCancel} disabled={saving}>Cancel</button>
        {document && isAdmin && !confirmDelete && (
          <button type="button" className="rm-button rm-button--danger cdoc-form__delete" onClick={() => setConfirmDelete(true)} disabled={saving}>
            <Icon name="trash" /> Delete
          </button>
        )}
        {document && isAdmin && confirmDelete && (
          <span className="cdoc-form__delete">
            <button type="button" className="rm-button rm-button--danger" onClick={remove} disabled={saving}>Delete it</button>
            <button type="button" className="rm-button rm-button--quiet" onClick={() => setConfirmDelete(false)} disabled={saving}>Keep</button>
          </span>
        )}
      </div>
    </form>
  );
};

const DocumentRow = ({ document, onEdit }) => {
  const expiry = expiryText(document);
  return (
    <li className="cdoc-row">
      <div className="cdoc-row__line">
        <span className="cdoc-kind">{document.kind_label}</span>
        <span className="cdoc-row__title" title={document.title}>{document.title}</span>
        <span className={`cdoc-pill cdoc-pill--${document.status}`}>{statusPillText(document)}</span>
        <button type="button" className="rm-icon-button" onClick={() => onEdit(document)}
          aria-label={`Edit ${document.title}`} title="Edit">
          <Icon name="pencil" />
        </button>
      </div>
      <div className="cdoc-row__meta">
        {expiry && <span className={`cdoc-badge${document.expired ? ' cdoc-badge--expired' : ''}`}>{expiry}</span>}
        {document.contact_name && <span>With {document.contact_name}</span>}
        {document.status_set_by && (
          <span title={formatDateTime(document.status_set_at)}>
            {document.status_source === 'manual' ? 'Set' : 'Confirmed'} by {document.status_set_by} {timeAgo(document.status_set_at)}
          </span>
        )}
        {document.drive_url && isDriveLink(document.drive_url) && (
          <a href={document.drive_url} target="_blank" rel="noopener noreferrer" className="cdoc-link">
            <Icon name="external-link" /> Drive
          </a>
        )}
      </div>
    </li>
  );
};

/**
 * A company's NDAs, MSAs and other documents, kept by hand: add one with +, change one with its
 * pencil. Each row says who set its status (from the audit log); an expiry date only shows a badge.
 */
const CompanyDocuments = ({ companyId, companyName, onDataChanged }) => {
  const [open, setOpen] = useState(true);
  const [documents, setDocuments] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [folderUrl, setFolderUrl] = useState(null);
  const [deciding, setDeciding] = useState(false);
  // 'new', a document id, or null
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    let cancelled = false;
    setError(null);
    documentsApi.forCompany(companyId)
      .then(data => {
        if (cancelled) return;
        setDocuments(data.documents);
        setFolderUrl(data.legal_folder_url || null);
      })
      .catch(err => { if (!cancelled) { setError(err.message); setDocuments([]); } });
    // Only a nicety on top of the list: it still works without them
    documentsApi.suggestions(companyId)
      .then(data => { if (!cancelled) setSuggestions(data.suggestions || []); })
      .catch(() => { if (!cancelled) setSuggestions([]); });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    setDocuments(null);
    setSuggestions([]);
    setFolderUrl(null);
    setEditing(null);
    return load();
  }, [load]);

  const saved = () => {
    setEditing(null);
    load();
    onDataChanged?.();
  };

  // Run a decision on a suggestion. A refusal (the record changed since) is shown on its card, and the list is
  // read again so the card and the record agree.
  const decide = async (action) => {
    setDeciding(true);
    try {
      await action();
      load();
      onDataChanged?.();
    } catch (err) {
      load();
      throw err;
    } finally {
      setDeciding(false);
    }
  };

  const count = documents ? documents.length : 0;
  return (
    <AccordionSection
      id="cdoc"
      className="cdoc"
      open={open}
      onToggle={() => setOpen(current => !current)}
      title={`Documents${documents && count > 0 ? ` (${count})` : ''}`}
      actions={(
        <>
          {folderUrl && (
            <a href={folderUrl} target="_blank" rel="noopener noreferrer" className="cdoc-link cdoc-folder">
              <Icon name="external-link" /> Folder
            </a>
          )}
          {editing !== 'new' && (
            <button type="button" className="rm-icon-button" onClick={() => { setOpen(true); setEditing('new'); }}
              aria-label={`Add a document for ${companyName || 'this company'}`} title="Add a document">
              <Icon name="plus" />
            </button>
          )}
        </>
      )}
    >
      {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      {documents === null && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>}

      {suggestions.length > 0 && (
        <ul className="cdoc-suggestions" aria-label="Suggested from the shared drive">
          {suggestions.map(suggestion => (
            <DocumentSuggestionCard
              key={suggestion.id}
              suggestion={suggestion}
              busy={deciding}
              onConfirm={(item, fields) => decide(() => documentsApi.confirmSuggestion(item.id, fields))}
              onDismiss={(item) => decide(() => documentsApi.dismissSuggestion(item.id))}
            />
          ))}
        </ul>
      )}

      {editing === 'new' && (
        <DocumentForm companyId={companyId} onSaved={saved} onDeleted={saved} onCancel={() => setEditing(null)} />
      )}

      {documents && documents.length === 0 && editing !== 'new' && !error && (
        <p className="rm-field__empty">
          No documents on file.{' '}
          <button type="button" className="rm-link" onClick={() => setEditing('new')}>Add one</button>
        </p>
      )}

      {documents && documents.length > 0 && (
        <ul className="cdoc-list">
          {documents.map(document => (
            editing === document.id ? (
              <li key={document.id} className="cdoc-row cdoc-row--editing">
                <DocumentForm companyId={companyId} document={document} onSaved={saved} onDeleted={saved}
                  onCancel={() => setEditing(null)} />
              </li>
            ) : <DocumentRow key={document.id} document={document} onEdit={(doc) => setEditing(doc.id)} />
          ))}
        </ul>
      )}
    </AccordionSection>
  );
};

export default CompanyDocuments;
